// 공용 `mm:ss` 입력 위젯 — **문법을 새로 만들지 않는다**
//
// 사람이 치는 글자를 읽는 것은 `src/core/timing/format.ts` 의 `parseTimeInput` 이고,
// 무엇이 유효한 시간인가의 판단은 `src/core/time/brand.ts` 하나가 내린다. 이 파일이 하는
// 일은 그 둘을 화면이 쓰기 좋은 모양으로 감싸는 것뿐이다.
//
// **왜 감싸는가.** 코어의 파서는 실패를 예외로 던진다. 화면은 매 글자마다 그것을 부르므로
// try 를 화면 코드 곳곳에 두르게 되고, 그러면 결국 「값이 잘못됐습니다」 한 문장으로
// 수렴한다 — 사용자에게 아무것도 알려 주지 않는 그 문장이 이 제품에서 가장 나쁘다.
// 그래서 예외를 판정 객체로 바꾸고 **거절 사유를 글자로 들고 다닌다.**
//
// ── 이 파일이 `ui/` 에 사는 이유 ────────────────────────────────────────────
//
// 수비의 「남은 rallying」 입력이 같은 형식을 받아야 한다. 이 위젯이 attack 디렉터리에
// 있으면 수비 갈래가 attack 을 import 하게 되고, 그 순간 두 갈래가 서로를 붙잡아 한쪽을
// 되돌릴 수 없게 된다. **수비 갈래는 이 위젯을 읽기만 하고 고치지 않는다.**
//
// ── 사람이 친 글자를 뭉개지 않는다 ─────────────────────────────────────────
//
// 값을 텍스트 그대로 부르는 쪽이 들고, 이 위젯은 그것을 그리기만 한다. 위젯이 매 글자마다
// 정규형으로 고쳐 되돌리면 `-0:00.5` 를 치는 도중의 `-0:00.` 이 사라져 사람이 소수점을
// 칠 수 없다. **읽는 시점은 사람이 다 친 뒤 한 번이다.**

import { duration, toMillis } from '../../core/time/brand.ts'
import { formatDuration, parseTimeInput } from '../../core/timing/format.ts'
import { type CommonStrings, common as commonStrings } from '../../i18n/index.ts'
import { useLang } from '../shell/use-lang.ts'

/** 왜 못 읽었는가. **문장이 아니라 사유다** — 글자는 화면이 고른다. */
export type TimeParseReason = 'needed' | 'malformed'

export type TimeParse = { ok: true; millis: number } | { ok: false; reason: TimeParseReason }

/**
 * 사람이 친 글자를 밀리초로 읽는다. **던지지 않고 판정을 돌려준다.**
 *
 * 빈 칸을 0 으로 읽지 않는 것이 요점이다. 「아직 안 넣었다」와 「0 을 넣었다」가 같은 값이
 * 되면 간격을 안 넣은 sub 가 main 과 정확히 같은 시각에 착탄하는 것으로 계산되고, 그 값은
 * 화면에서 정상으로 보인다. 명부가 같은 함정을 빈 marching 에서 이미 만났다.
 *
 * ── 문장을 돌려주지 않는다 (2026-08-17) ────────────────────────────────────
 *
 * 이 함수는 한동안 한국어 문장을 만들어 돌려줬고 형식 안내도 모듈 상수로 들고 있었다.
 * 그래서 **영어 화면에서도 착탄 간격 칸 아래가 한국어였다** — 그것도 CT 가 이 제품에서
 * 가장 자주 읽는 자리다. 서버 거절이 코드를 지나게 된 것과 같은 문법으로 옮겼다.
 *
 * 코어의 예외 문구를 싣지 않는 판단은 그대로다 — 그쪽은 개발자에게 하는 말이라 정규식과
 * JSON 이 섞여 있고, 이 자리는 전투 직전의 CT 가 읽는다.
 */
export function parseTimeText(text: string): TimeParse {
  if (text.trim() === '') return { ok: false, reason: 'needed' }
  try {
    return { ok: true, millis: toMillis(parseTimeInput(text)) }
  } catch {
    return { ok: false, reason: 'malformed' }
  }
}

/** 그 사유를 지금 언어의 한 문장으로. **형식 안내를 늘 함께 붙인다** — 무엇이 틀렸는지만
 * 말하고 무엇이 맞는지를 안 말하면 사람은 두 번째도 틀린다. */
export function timeParseMessage(reason: TimeParseReason, t: CommonStrings): string {
  // 사유와 형식 안내를 대시로 잇지 않고 두 문장으로 둔다(2026-10-14 사용자 지시, 문구 정리).
  return `${reason === 'needed' ? t.timeNeeded : t.timeMalformed}. ${t.timeFormatHint}`
}

/**
 * 밀리초를 사람이 읽는 글자로. **제품 형식이지 정규형이 아니다.**
 *
 * `formatDuration` 은 `4:00.0` 처럼 분 자리에 앞의 0 을 채우지 않는다. `brand.ts` 의
 * `formatMmss` 는 저장·전송 경계의 정규형이라 `04:00.0` 을 내놓고, 그것이 화면에 뜨면
 * 형식이 두 벌인 것이다.
 */
export function formatTimeText(millis: number): string {
  return formatDuration(duration(millis))
}

export interface TimeInputProps {
  testId: string
  /**
   * 칸 위에 적히는 말. **부르는 쪽이 정한다.**
   *
   * 정본이 착탄 간격 칸의 라벨을 「착탄 offset  = main 착탄 + Δ」로 원문 고정했다 —
   * 착탄축이라는 사실을 말로 설명하는 대신 수식으로 못박은 형태다. 위젯이 자기 말을
   * 우겨 넣으면 그 계약을 지킬 자리가 없어진다.
   */
  label: string
  value: string
  onChange: (next: string) => void
  placeholder?: string
  /** 이 칸이 지금 하는 말. 거절 사유가 여기 온다. */
  message?: string
  /** 칸 아래 형식 안내를 감춘다. 하단 고정 바처럼 자리가 좁은 곳에서만 쓴다. */
  compact?: boolean
  disabled?: boolean
}

export function TimeInput(props: TimeInputProps): React.ReactElement {
  const t = commonStrings(useLang().lang)
  const bad = props.message !== undefined && props.message !== ''
  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs">
      <span className="font-medium text-ink-dim">{props.label}</span>
      <input
        data-testid={props.testId}
        // 숫자가 세로로 맞아야 목록에서 값을 비교할 수 있다. 자릿수가 흔들리면 사람은
        // 두 값 중 어느 쪽이 큰지를 읽지 못하고 세어야 한다.
        className={`field px-2.5 font-mono text-sm font-medium tabular-nums ${
          bad ? 'border-red-400' : ''
        } ${props.disabled === true ? 'text-ink-off' : ''}`}
        value={props.value}
        placeholder={props.placeholder ?? '0:00.0'}
        // 폰에서 숫자 자판이 먼저 뜨게 한다. `numeric` 이 아니라 `decimal` 인 이유는
        // 소수점이 필요하기 때문이다.
        inputMode="decimal"
        autoComplete="off"
        disabled={props.disabled}
        onChange={(event) => {
          // **고쳐서 되돌리지 않는다.** 매 글자마다 정규형으로 바꾸면 `-0:00.` 처럼
          // 아직 안 끝난 글자가 사라져 소수점을 칠 수 없다.
          props.onChange(event.target.value)
        }}
      />
      {/* 형식을 미리 말한다. 실패한 뒤에 배우게 하면 그 한 번이 전투 직전이다. */}
      {props.compact !== true && <span className="text-ink-mute">{t.timeFormatHint}</span>}
      {bad && (
        <span role="alert" className="text-red-300">
          {props.message}
        </span>
      )}
    </label>
  )
}
